import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  MenuItem,
  Autocomplete,
  Alert,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { Client, Trade, TradeType, TradeCreateInput } from '../../types';
import { tradeService } from '../../services/trade.service';
import { calculateChargesBreakdown, DEFAULT_OPTION_BROKERAGE as DEFAULT_OPTION_BROKERAGE_FALLBACK } from '../../hooks/useTradeCalculations';
import { TRADE_TYPES, COMMON_SCRIPS, CHARGE_RATES } from '../../utils/constants';
import { marketService, FnOScrip } from '../../services/market.service';

// ============================================================================
// IST TIME HELPERS
// ============================================================================

function getISTNow(): string {
  const ist = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().slice(0, 16);
}

function toISTLocal(isoStr: string): string {
  const d = new Date(isoStr);
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().slice(0, 16);
}

function istLocalToISO(localStr: string): string {
  return new Date(localStr + ':00+05:30').toISOString();
}

// ============================================================================
// FORM TYPES & DEFAULTS
// ============================================================================

interface TradeForm {
  clientId: string;
  tradeType: string;
  direction: 'BUY' | 'SELL';
  exchange: 'NSE' | 'BSE';
  isMTF: boolean;
  leverage: string;
  buyDate: string;
  scrip: string;
  purchaseLots: number | '';
  lotSize: number | '';
  purchasePrice: number | '';
  stopLoss: number | '';
  closeTrade: boolean;
  saleDate: string;
  saleLots: number | '';
  salePrice: number | '';
}

export const EMPTY_FORM: TradeForm = {
  clientId: '',
  tradeType: 'Equity',
  direction: 'BUY',
  exchange: 'NSE',
  isMTF: false,
  leverage: '',
  buyDate: getISTNow(),
  scrip: '',
  purchaseLots: '',
  lotSize: 1,
  purchasePrice: '',
  stopLoss: '',
  closeTrade: false,
  saleDate: getISTNow(),
  saleLots: '',
  salePrice: '',
};

export function buildFormFromTrade(trade: Trade): TradeForm {
  const isEquity = trade.tradeType === TradeType.EQUITY || trade.tradeType === TradeType.STOCK || trade.tradeType === TradeType.INTRA_STOCK;
  return {
    clientId: trade.clientId,
    tradeType: isEquity ? 'Equity' : trade.tradeType === TradeType.FUTURE ? 'Future' : 'Option',
    direction: trade.direction || 'BUY',
    exchange: trade.exchange || 'NSE',
    isMTF: trade.isMTF || false,
    leverage: trade.leverage || '',
    buyDate: trade.date ? toISTLocal(trade.date) : getISTNow(),
    scrip: trade.scrip,
    purchaseLots: trade.purchaseLots,
    lotSize: trade.lotSize,
    purchasePrice: trade.purchasePrice,
    stopLoss: trade.stopLoss || '',
    closeTrade: false,
    saleDate: getISTNow(),
    saleLots: trade.saleLots || '',
    salePrice: trade.salePrice || '',
  };
}

// ============================================================================
// PROPS
// ============================================================================

interface BreakevenInfo {
  intradayBE?: number;
  deliveryBE?: number;
  fullClearBE?: number;
  carryForwardBE?: number;
  futureBE?: number;
  optionBE?: number;
}

interface TradeDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clients: Client[];
  editTrade?: Trade | null;
  existingTrades?: Trade[];
  prefillData?: { clientId: string; scrip: string; tradeType: string; direction?: 'BUY' | 'SELL' };
  /**
   * Optional text to pre-populate in the client search field.
   * Used by the dashboard quick-entry shortcut (type any letter → opens dialog
   * with that letter already entered as the client filter).
   */
  prefillClientQuery?: string;
  breakeven?: BreakevenInfo;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function TradeDialog({ open, onClose, onSuccess, clients, editTrade, existingTrades, prefillData, prefillClientQuery, breakeven }: TradeDialogProps) {
  const isEdit = !!editTrade;
  const [form, setForm] = useState<TradeForm>(isEdit ? buildFormFromTrade(editTrade!) : { ...EMPTY_FORM, buyDate: getISTNow(), saleDate: getISTNow() });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Client autocomplete search text — decoupled from form.clientId so users can
  // type freely and pick from filtered suggestions.
  const [clientQuery, setClientQuery] = useState('');
  const clientInputRef = useRef<HTMLInputElement | null>(null);
  const scripInputRef = useRef<HTMLInputElement | null>(null);

  // F&O scrips from Kite API (fetched once, cached in state)
  const [fnoScrips, setFnoScrips] = useState<FnOScrip[]>([]);
  const [fnoLoading, setFnoLoading] = useState(false);

  // All NSE equity scrips from Kite API (~2900 symbols)
  const [equityScrips, setEquityScrips] = useState<string[]>([]);
  const [equityLoading, setEquityLoading] = useState(false);

  // NFO instruments (for options: CE/PE with strike/expiry)
  const [nfoInstruments, setNfoInstruments] = useState<any[]>([]);
  const [nfoLoading, setNfoLoading] = useState(false);

  // Option selection state
  const [optionExpiry, setOptionExpiry] = useState<string>('');
  const [optionStrike, setOptionStrike] = useState<number | ''>('');
  const [optionType, setOptionType] = useState<'CE' | 'PE'>('CE');

  // Track whether user has manually edited the price (prevents auto-fetch override)
  const [priceManuallyEdited, setPriceManuallyEdited] = useState(false);

  // Reset form when dialog opens / editTrade changes
  useEffect(() => {
    if (open) {
      const lookupClientName = (id: string) => {
        const c = clients.find((x) => x.clientId === id);
        return c ? c.name : '';
      };
      if (isEdit) {
        setForm(buildFormFromTrade(editTrade!));
        setClientQuery(lookupClientName(editTrade!.clientId));
      } else if (prefillData) {
        // "Add to Position" — pre-fill client, scrip, type, direction
        setForm({ ...EMPTY_FORM, buyDate: getISTNow(), saleDate: getISTNow(), clientId: prefillData.clientId, scrip: prefillData.scrip, tradeType: prefillData.tradeType, direction: prefillData.direction || 'BUY' });
        setClientQuery(lookupClientName(prefillData.clientId));
      } else {
        setForm({ ...EMPTY_FORM, buyDate: getISTNow(), saleDate: getISTNow() });
        // Quick-entry: open dialog with typed query already filtering the client list
        setClientQuery(prefillClientQuery || '');
      }
      setError(null);
    }
  }, [open, editTrade, isEdit, prefillData, prefillClientQuery, clients]);

  // Auto-focus the client search field when the dialog opens for a fresh trade
  // so keystroke-to-open flows land directly inside the input.
  useEffect(() => {
    if (open && !isEdit && !prefillData) {
      const t = setTimeout(() => {
        const el = clientInputRef.current;
        if (el) {
          try {
            el.focus();
            // Place caret at end so appended characters go where expected
            const v = el.value;
            el.setSelectionRange(v.length, v.length);
          } catch {}
        }
      }, 60);
      return () => clearTimeout(t);
    }
  }, [open, isEdit, prefillData, prefillClientQuery]);

  // Derived state
  const isEquity = form.tradeType === 'Equity';
  const isFnO = form.tradeType === 'Future' || form.tradeType === 'Option';
  const isSellFirst = form.direction === 'SELL';
  // Direction-aware labels
  const openLabel = isSellFirst ? 'Sell' : 'Buy';
  const closeLabel = isSellFirst ? 'Buy Back' : 'Sell';
  const hasSale = form.closeTrade || (isEdit && Number(form.saleLots) > 0 && Number(form.salePrice) > 0);
  const showSaleSection = hasSale || (isEdit && editTrade && editTrade.saleLots > 0);

  // Fetch F&O scrips from API when trade type changes to Future/Option
  useEffect(() => {
    if (isFnO && fnoScrips.length === 0 && !fnoLoading) {
      setFnoLoading(true);
      marketService.getFnOScrips()
        .then((res) => setFnoScrips(res.fnoScrips || []))
        .catch((err) => console.warn('Failed to fetch F&O scrips:', err))
        .finally(() => setFnoLoading(false));
    }
  }, [isFnO, fnoScrips.length, fnoLoading]);

  // Fetch ALL NSE equity instruments (~2900 symbols) when equity trade is opened
  useEffect(() => {
    if (open && isEquity && equityScrips.length === 0 && !equityLoading) {
      setEquityLoading(true);
      marketService.getInstruments({ exchange: 'NSE' })
        .then((res) => {
          // Keep unique trading symbols only, filter out obvious bonds/junk
          const syms = (res.instruments || [])
            .map((i: any) => i.tradingsymbol)
            .filter((s: string) => s && !/^\d/.test(s)) // drop bonds starting with digit
            .sort();
          setEquityScrips([...new Set(syms)] as string[]);
        })
        .catch((err) => console.warn('Failed to fetch NSE equity scrips:', err))
        .finally(() => setEquityLoading(false));
    }
  }, [open, isEquity, equityScrips.length, equityLoading]);

  // Fetch ALL NFO option contracts when Option trade is opened AND underlying is selected
  const isOption = form.tradeType === 'Option';
  // Extract "pure" underlying (leading A-Z only) — used to detect a real underlying change
  const optionUnderlying = isOption ? (form.scrip.match(/^[A-Z_-]+/)?.[0] || '') : '';
  const [lastFetchedUnderlying, setLastFetchedUnderlying] = useState('');
  useEffect(() => {
    if (!open || !isOption || !optionUnderlying) return;
    if (optionUnderlying === lastFetchedUnderlying) return; // already fetched for this underlying
    if (nfoLoading) return;
    setNfoLoading(true);
    marketService.getInstruments({ exchange: 'NFO', underlying: optionUnderlying })
      .then((res) => {
        setNfoInstruments(res.instruments || []);
        setLastFetchedUnderlying(optionUnderlying);
      })
      .catch((err) => console.warn('Failed to fetch NFO contracts:', err))
      .finally(() => setNfoLoading(false));
  }, [open, isOption, optionUnderlying, lastFetchedUnderlying, nfoLoading]);

  // When underlying changes (not the full contract symbol), clear option selections
  useEffect(() => {
    if (isOption && optionUnderlying !== lastFetchedUnderlying) {
      setOptionExpiry('');
      setOptionStrike('');
    }
  }, [isOption, optionUnderlying, lastFetchedUnderlying]);

  // Resolve full option contract symbol when all 3 selectors are picked
  useEffect(() => {
    if (!isOption || !optionUnderlying || !optionExpiry || !optionStrike || !optionType) return;
    const match = nfoInstruments.find((i: any) =>
      (i.name === optionUnderlying || i.tradingsymbol?.startsWith(optionUnderlying)) &&
      i.expiry === optionExpiry &&
      Number(i.strike) === Number(optionStrike) &&
      i.instrument_type === optionType
    );
    if (match && form.scrip !== match.tradingsymbol) {
      setForm((prev) => ({
        ...prev,
        scrip: match.tradingsymbol,
        lotSize: match.lot_size || prev.lotSize,
      }));
    }
  }, [isOption, optionUnderlying, optionExpiry, optionStrike, optionType, nfoInstruments, form.scrip]);

  // Auto-populate lot size from Kite API data (F&O)
  // For options, form.scrip may be the full contract (e.g. SHREECEM26APR24000CE) — extract the underlying first
  useEffect(() => {
    if (isFnO && form.scrip) {
      const upper = form.scrip.toUpperCase();
      const underlying = upper.match(/^[A-Z_-]+/)?.[0] || upper;
      const apiMatch = fnoScrips.find((s) => s.symbol === underlying);
      if (apiMatch && apiMatch.lotSize > 1) {
        setForm((prev) => {
          if (prev.lotSize === apiMatch.lotSize) return prev; // already correct, avoid infinite loop
          return { ...prev, lotSize: apiMatch.lotSize };
        });
      }
    }
  }, [form.scrip, isFnO, fnoScrips]);

  // When equity, force lotSize=1
  useEffect(() => {
    if (isEquity) {
      setForm((prev) => ({ ...prev, lotSize: 1 }));
    }
  }, [isEquity]);

  // Auto-fetch live price when scrip changes (new trades only, skip if user has manually edited price)
  // For options, we ONLY fetch once the full contract symbol is resolved (e.g. NIFTY25APR2424000CE)
  // — not while the user is still picking underlying/expiry/strike/CE-PE — to avoid briefly showing
  // the underlying index price instead of the option premium.
  useEffect(() => {
    if (!open || isEdit || !form.scrip || form.scrip.length < 2) return;
    if (priceManuallyEdited) return; // user has manually set the price — don't override

    // For options: require a fully-resolved contract symbol (ends in CE/PE with digits)
    if (isOption) {
      const looksLikeContract = /(?:CE|PE)$/.test(form.scrip.toUpperCase()) && /\d/.test(form.scrip);
      if (!looksLikeContract) return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { prices } = await marketService.getQuotes([form.scrip.toUpperCase()]);
        const q = prices[form.scrip.toUpperCase()];
        if (q && q.price > 0 && !cancelled && !priceManuallyEdited) {
          setForm((prev) => ({ ...prev, purchasePrice: Math.round(q.price * 100) / 100 }));
        }
      } catch {
        // Non-fatal — user can still type the price manually
      }
    }, 300); // Small debounce so we don't fire on every keystroke

    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.scrip, open, isEdit, priceManuallyEdited, isOption]);

  // Reset the "manually edited" flag when the dialog opens or scrip changes
  useEffect(() => {
    if (!open) return;
    setPriceManuallyEdited(false);
  }, [open, form.scrip]);

  // Pre-fill saleLots from purchaseLots when closeTrade toggled on
  useEffect(() => {
    if (form.closeTrade) {
      setForm((prev) => ({
        ...prev,
        saleLots: prev.purchaseLots,
        saleDate: getISTNow(),
      }));
    }
  }, [form.closeTrade, isEdit]);

  // Pre-fill buy price from breakeven when creating new trade for existing position
  useEffect(() => {
    if (!isEdit && form.clientId && form.scrip && existingTrades && existingTrades.length > 0 && !priceManuallyEdited) {
      const matching = existingTrades.filter(t =>
        t.clientId === form.clientId &&
        t.scrip.toUpperCase() === form.scrip.toUpperCase() &&
        t.status === 'OPEN' &&
        !t.isDeleted
      );
      if (matching.length > 0) {
        // Simple weighted average buy price + estimated charges per share
        const totalShares = matching.reduce((s, t) => s + t.balanceLots * t.lotSize, 0);
        const totalCost = matching.reduce((s, t) => s + t.balanceLots * t.lotSize * t.purchasePrice, 0);
        if (totalShares > 0) {
          const avgPrice = totalCost / totalShares;
          // Add ~0.5% estimated charges
          const prefillPrice = Math.round(avgPrice * 1.005 * 100) / 100;
          setForm(prev => ({ ...prev, purchasePrice: prefillPrice }));
        }
      }
    }
  }, [form.clientId, form.scrip, isEdit, existingTrades, priceManuallyEdited]);

  const updateField = <K extends keyof TradeForm>(key: K, value: TradeForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Charges calculation — determine correct type from dates, show both intraday & delivery for equity
  // For sell-first: open side is a sell, close side is a buy → swap buyValue/sellValue in charges
  const chargesData = (() => {
    const purchaseLots = Number(form.purchaseLots) || 0;
    const saleLots = Number(form.saleLots) || 0;
    const salePrice = Number(form.salePrice) || 0;
    const purchasePrice = Number(form.purchasePrice) || 0;
    const lotSize = Number(form.lotSize) || 1;
    const isSF = form.direction === 'SELL';

    if (purchaseLots <= 0 || purchasePrice <= 0) return null;

    const hasSale = saleLots > 0 && salePrice > 0;
    const isEquity = form.tradeType === 'Equity';

    const client = clients.find((c) => c.clientId === form.clientId);
    const intraRate = client?.brokerageRates?.intraStock ?? 0.0003;
    const deliveryRate = client?.brokerageRates?.stock ?? 0.003;

    if (isEquity) {
      // Determine if this is intraday based on buy date vs today (for buy-only) or buy vs sale date
      const buyDateStr = form.buyDate.slice(0, 10);
      const todayStr = getISTNow().slice(0, 10);
      const isBuyToday = buyDateStr === todayStr;
      const isIntraday = hasSale ? (buyDateStr === form.saleDate.slice(0, 10)) : isBuyToday;

      const openValue = hasSale ? saleLots * lotSize * purchasePrice : purchaseLots * lotSize * purchasePrice;
      const closeValue = hasSale ? saleLots * lotSize * salePrice : 0;

      // Primary charges: based on actual dates
      // For sell-first: open is sell, close is buy → swap args
      const exch = form.exchange || 'NSE';
      const primaryType = isIntraday ? TradeType.INTRA_STOCK : TradeType.EQUITY;
      const primaryRate = isIntraday ? intraRate : deliveryRate;
      const primaryBuy = isSF
        ? calculateChargesBreakdown(primaryType, 0, openValue, primaryRate, isIntraday, exch)
        : calculateChargesBreakdown(primaryType, openValue, 0, primaryRate, isIntraday, exch);
      const primarySell = hasSale
        ? (isSF
          ? calculateChargesBreakdown(primaryType, closeValue, 0, primaryRate, isIntraday, exch)
          : calculateChargesBreakdown(primaryType, 0, closeValue, primaryRate, isIntraday, exch))
        : null;
      const primaryTotal = hasSale
        ? (isSF
          ? calculateChargesBreakdown(primaryType, closeValue, openValue, primaryRate, isIntraday, exch)
          : calculateChargesBreakdown(primaryType, openValue, closeValue, primaryRate, isIntraday, exch))
        : null;

      const pct = (primaryRate * 100).toFixed(2).replace(/\.?0+$/, '') + '%';
      return {
        isEquity: true, hasSale, isIntraday,
        primaryBuy, primarySell, primaryTotal,
        primaryLabel: isIntraday ? `Intraday (${pct})` : `Delivery (${pct})`,
        primaryColor: isIntraday ? '#059669' : '#2563EB',
        primaryRate, exch,
      };
    }

    // F&O: single charges
    const tradeType: TradeType = form.tradeType === 'Future' ? TradeType.FUTURE : TradeType.OPTION;
    const brokerageRate = tradeType === TradeType.FUTURE
      ? (client?.brokerageRates?.future || CHARGE_RATES.DEFAULT_FUTURE_BROKERAGE)
      : CHARGE_RATES.DEFAULT_OPTION_BROKERAGE;
    const exch = form.exchange || 'NSE';

    // Options: per-lot brokerage from client's brokerageRates.option (not hardcoded dictionary)
    const isOption = tradeType === TradeType.OPTION;
    const optionPerLot = isOption ? (client?.brokerageRates?.option || DEFAULT_OPTION_BROKERAGE_FALLBACK) : 0;
    const buyLots = purchaseLots; // for F&O, purchaseLots = number of lots
    const sellLotsCount = saleLots;
    const buyFixedBrk = isOption ? optionPerLot * buyLots : undefined;
    const sellFixedBrk = isOption ? optionPerLot * sellLotsCount : undefined;

    if (hasSale) {
      const openVal = sellLotsCount * lotSize * purchasePrice;
      const closeVal = sellLotsCount * lotSize * salePrice;
      // For sell-first: open is sell, close is buy → swap
      const chargeBuyVal = isSF ? closeVal : openVal;
      const chargeSellVal = isSF ? openVal : closeVal;
      const charges = calculateChargesBreakdown(tradeType, chargeBuyVal, chargeSellVal, brokerageRate, false, exch, isOption ? optionPerLot * sellLotsCount * 2 : undefined);
      const chargesBuy = calculateChargesBreakdown(tradeType, chargeBuyVal, 0, brokerageRate, false, exch, isSF ? sellFixedBrk : buyFixedBrk);
      const chargesSell = calculateChargesBreakdown(tradeType, 0, chargeSellVal, brokerageRate, false, exch, isSF ? buyFixedBrk : sellFixedBrk);
      return { isEquity: false, hasSale, charges, chargesBuy, chargesSell, tradeType, brokerageRate, saleLots: sellLotsCount, purchaseLots: buyLots, lotSize, isOption, optionPerLot };
    } else {
      const openVal = buyLots * lotSize * purchasePrice;
      // For sell-first open-only: the open side is a sell
      const charges = isSF
        ? calculateChargesBreakdown(tradeType, 0, openVal, brokerageRate, false, exch, buyFixedBrk)
        : calculateChargesBreakdown(tradeType, openVal, 0, brokerageRate, false, exch, buyFixedBrk);
      return { isEquity: false, hasSale, charges, tradeType, brokerageRate, saleLots: sellLotsCount, purchaseLots: buyLots, lotSize, isOption, optionPerLot };
    }
  })();

  // Auto equity/intraday detection label
  const effectiveTradeType = (() => {
    if (form.tradeType !== 'Equity') return form.tradeType;
    if (!showSaleSection) return 'Equity';
    const buyDateStr = form.buyDate.slice(0, 10);
    const saleDateStr = form.saleDate.slice(0, 10);
    return buyDateStr === saleDateStr ? 'Intra Stock' : 'Equity';
  })();

  // Auto-split detection: equity same-day partial sale → 2 trades
  const willAutoSplit = (() => {
    if (form.tradeType !== 'Equity' || !showSaleSection) return false;
    const buyDay = form.buyDate.slice(0, 10);
    const saleDay = form.saleDate.slice(0, 10);
    if (buyDay !== saleDay) return false;
    const pLots = Number(form.purchaseLots) || 0;
    const sLots = Number(form.saleLots) || 0;
    return sLots > 0 && sLots < pLots; // partial same-day sale
  })();
  const splitIntradayQty = willAutoSplit ? Number(form.saleLots) : 0;
  const splitDeliveryQty = willAutoSplit ? (Number(form.purchaseLots) - Number(form.saleLots)) : 0;

  // Submit handler
  const handleSubmit = async () => {
    // Validation
    if (!form.clientId) { setError('Client is required'); return; }
    if (!form.scrip) { setError('Scrip is required'); return; }
    if (!form.purchaseLots || Number(form.purchaseLots) <= 0) { setError(`${openLabel} quantity is required`); return; }
    if (!form.purchasePrice || Number(form.purchasePrice) <= 0) { setError(`${openLabel} price is required`); return; }

    setLoading(true);
    setError(null);

    try {
      // Determine final trade type
      let finalTradeType: TradeType;
      if (form.tradeType === 'Equity') {
        if (showSaleSection) {
          const buyDateStr = form.buyDate.slice(0, 10);
          const saleDateStr = form.saleDate.slice(0, 10);
          finalTradeType = buyDateStr === saleDateStr ? TradeType.INTRA_STOCK : TradeType.EQUITY;
        } else {
          finalTradeType = TradeType.EQUITY;
        }
      } else if (form.tradeType === 'Future') {
        finalTradeType = TradeType.FUTURE;
      } else {
        finalTradeType = TradeType.OPTION;
      }

      const payload: TradeCreateInput = {
        clientId: form.clientId,
        tradeType: finalTradeType,
        date: istLocalToISO(form.buyDate),
        scrip: form.scrip,
        purchaseLots: Number(form.purchaseLots),
        lotSize: Number(form.lotSize) || 1,
        purchasePrice: Number(form.purchasePrice),
        direction: form.direction,
        exchange: form.exchange || 'NSE',
        isMTF: form.isMTF || false,
        leverage: form.isMTF ? form.leverage : undefined,
        stopLoss: form.stopLoss ? Number(form.stopLoss) : undefined,
        saleDate: showSaleSection ? istLocalToISO(form.saleDate) : undefined,
        saleLots: showSaleSection ? Number(form.saleLots) || 0 : 0,
        salePrice: showSaleSection ? Number(form.salePrice) || 0 : 0,
      };

      if (isEdit && editTrade) {
        await tradeService.updateTrade(editTrade.tradeId, payload);
      } else {
        await tradeService.createTrade(payload);
      }

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to save trade');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth key={editTrade ? editTrade.tradeId : 'new'} PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      {/* Header */}
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#0A0A0A', color: '#fff', py: 1.5, px: 2.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>
            {isEdit ? 'Edit Trade' : 'New Trade'}
          </Typography>
          {effectiveTradeType !== form.tradeType && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>Auto: {effectiveTradeType}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      {/* Subtle trade info bar (edit mode only) */}
      {isEdit && editTrade && (
        <Box sx={{ px: 2.5, py: 0.8, bgcolor: '#F8F7FF', borderBottom: '1px solid #E5E7EB', display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip label={form.tradeType} size="small" sx={{ fontSize: '0.65rem', fontWeight: 700, height: 20, bgcolor: form.tradeType === 'Equity' ? '#DBEAFE' : form.tradeType === 'Future' ? '#FEF3C7' : '#EDE9FE', color: form.tradeType === 'Equity' ? '#1D4ED8' : form.tradeType === 'Future' ? '#92400E' : '#6D28D9' }} />
          <Chip label={editTrade.direction || 'BUY'} size="small" sx={{ fontSize: '0.65rem', fontWeight: 700, height: 20, bgcolor: editTrade.direction === 'SELL' ? '#FEE2E2' : '#D1FAE5', color: editTrade.direction === 'SELL' ? '#DC2626' : '#059669' }} />
          <Chip label={editTrade.exchange || 'NSE'} size="small" variant="outlined" sx={{ fontSize: '0.6rem', fontWeight: 600, height: 20, borderColor: '#D1D5DB', color: '#6B7280' }} />
          {editTrade.isMTF && (
            <Chip label={`MTF ${editTrade.leverage || ''}`} size="small" sx={{ fontSize: '0.6rem', fontWeight: 700, height: 20, bgcolor: '#FEF3C7', color: '#92400E' }} />
          )}
        </Box>
      )}

      <DialogContent sx={{ pt: '20px !important', px: 2.5, pb: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: '0.8rem' }}>{error}</Alert>}

        <Grid container spacing={2}>
          {/* Row 1: Client + Type + Direction + Exchange */}
          <Grid item xs={5}>
            {(() => {
              const activeClients = clients.filter(c => c.isActive !== false);
              const selectedClient = activeClients.find(c => c.clientId === form.clientId) || null;
              return (
                <Autocomplete
                  size="small"
                  autoHighlight
                  openOnFocus
                  disableClearable
                  forcePopupIcon={false}
                  options={activeClients}
                  value={selectedClient as any}
                  inputValue={clientQuery}
                  onInputChange={(_, val, reason) => {
                    // Only commit user-driven edits. 'reset' fires when MUI
                    // reconciles the input with the selected value on mount —
                    // if we accepted it, it would wipe the seeded quick-entry
                    // character the moment the dialog opens.
                    if (reason === 'input' || reason === 'clear') {
                      setClientQuery(val);
                    }
                  }}
                  onChange={(_, val) => {
                    updateField('clientId', val ? (val as Client).clientId : '');
                    if (val) setClientQuery((val as Client).name);
                  }}
                  getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.name || '')}
                  isOptionEqualToValue={(opt, val) => opt.clientId === (val as Client).clientId}
                  filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    if (!q) return opts;
                    return opts.filter((o) => o.name.toLowerCase().includes(q));
                  }}
                  noOptionsText={clientQuery ? 'No clients match' : 'Start typing a name…'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client *"
                      placeholder="Type to search…"
                      inputRef={clientInputRef}
                      onKeyDown={(e) => {
                        if (e.key === 'Tab' && !e.shiftKey) {
                          e.preventDefault();
                          setTimeout(() => scripInputRef.current?.focus(), 0);
                        }
                      }}
                    />
                  )}
                />
              );
            })()}
          </Grid>
          <Grid item xs={2.5}>
            <TextField select fullWidth size="small" label="Type" value={form.tradeType} onChange={e => updateField('tradeType', e.target.value)}>
              {TRADE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={2.5}>
            <TextField select fullWidth size="small" label="Direction" value={form.direction} onChange={e => updateField('direction', e.target.value as 'BUY' | 'SELL')}
              sx={{ '& .MuiSelect-select': { color: form.direction === 'BUY' ? '#059669' : '#DC2626', fontWeight: 700 } }}>
              <MenuItem value="BUY" sx={{ color: '#059669', fontWeight: 600 }}>BUY</MenuItem>
              <MenuItem value="SELL" sx={{ color: '#DC2626', fontWeight: 600 }}>SELL</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={2}>
            <ToggleButtonGroup
              value={form.exchange}
              exclusive
              onChange={(_, val) => { if (val) updateField('exchange', val as 'NSE' | 'BSE'); }}
              size="small"
              sx={{ height: 40, '& .MuiToggleButton-root': { px: 1, py: 0.5, fontSize: '0.65rem', fontWeight: 700, lineHeight: 1, textTransform: 'none' }, '& .Mui-selected': { bgcolor: '#7C3AED !important', color: '#fff !important' } }}
            >
              <ToggleButton value="NSE">NSE</ToggleButton>
              <ToggleButton value="BSE">BSE</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* MTF + Leverage — compact inline (Equity only) */}
          {isEquity && (
            <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FormControlLabel
                control={<Switch size="small" checked={form.isMTF} onChange={e => { updateField('isMTF', e.target.checked); if (!e.target.checked) updateField('leverage', ''); }} />}
                label={<Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: form.isMTF ? '#7C3AED' : '#9CA3AF' }}>MTF</Typography>}
                sx={{ ml: 0, mr: 0, '& .MuiSwitch-root': { mr: 0.3 } }}
              />
              {form.isMTF && (
                <TextField select size="small"
                  value={['1x','2x','3x','4x','5x','6x','7x','8x','9x','10x'].includes(form.leverage) ? form.leverage : (form.leverage ? 'custom' : '')}
                  onChange={e => updateField('leverage', e.target.value === 'custom' ? 'custom' : e.target.value)}
                  sx={{ minWidth: 70, '& .MuiInputBase-root': { fontSize: '0.7rem', height: 30 }, '& .MuiSelect-select': { py: '4px' } }}>
                  {['1x', '2x', '3x', '4x', '5x', '6x', '7x', '8x', '9x', '10x'].map(l => <MenuItem key={l} value={l} sx={{ fontSize: '0.75rem' }}>{l}</MenuItem>)}
                  <MenuItem value="custom" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>Custom</MenuItem>
                </TextField>
              )}
              {form.isMTF && (form.leverage === 'custom' || (form.leverage && !['1x','2x','3x','4x','5x','6x','7x','8x','9x','10x',''].includes(form.leverage))) && (
                <TextField size="small" placeholder="e.g. 12x"
                  value={form.leverage === 'custom' ? '' : form.leverage}
                  onChange={e => { const v = e.target.value.replace(/[^0-9.x]/g, ''); updateField('leverage', v || 'custom'); }}
                  onBlur={() => { if (form.leverage === 'custom') updateField('leverage', ''); }}
                  sx={{ width: 65, '& .MuiInputBase-root': { fontSize: '0.7rem', height: 30 }, '& input': { py: '4px' } }} />
              )}
            </Grid>
          )}

          {/* Row 2: Date + Scrip */}
          <Grid item xs={6}>
            <TextField fullWidth size="small" label={`${openLabel} Date`} type="datetime-local" value={form.buyDate} onChange={e => updateField('buyDate', e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              freeSolo
              size="small"
              options={
                // For F&O (futures + options): ONLY F&O-eligible underlyings from Kite (never fall back to all NSE scrips)
                // For equity: all ~2900 NSE symbols, with a tiny in-memory fallback while loading
                isFnO
                  ? fnoScrips.map(s => s.symbol)
                  : (equityScrips.length > 0 ? equityScrips : COMMON_SCRIPS)
              }
              loading={(isEquity && equityLoading) || (isFnO && fnoLoading)}
              loadingText={isFnO ? 'Loading F&O scrips from Kite…' : 'Loading NSE scrips from Kite…'}
              noOptionsText={isFnO && !fnoLoading ? 'No F&O contracts match' : 'No match'}
              // For options, the Autocomplete displays only the underlying; the contract symbol lives in form.scrip
              inputValue={isOption ? optionUnderlying : form.scrip}
              // onChange fires when the user picks an option from the dropdown (click or Enter)
              onChange={(_, val) => {
                const picked = typeof val === 'string' ? val : (val as any)?.symbol || '';
                if (picked) updateField('scrip', picked.toUpperCase());
              }}
              // onInputChange fires on every keystroke; we allow free-text but skip programmatic resets
              // that happen after we set form.scrip ourselves (which would wipe the user's selection)
              onInputChange={(_, val, reason) => {
                if (reason === 'input') {
                  updateField('scrip', (val || '').toUpperCase());
                }
              }}
              renderInput={params => <TextField {...params} label={isOption ? 'Underlying *' : 'Scrip *'} inputRef={scripInputRef} helperText={isOption ? 'Pick underlying then select expiry + strike + CE/PE below' : undefined} />}
            />
          </Grid>

          {/* Options: Expiry + Strike + CE/PE selector */}
          {isOption && form.scrip && (
            <>
              <Grid item xs={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Expiry *"
                  value={optionExpiry}
                  onChange={e => { setOptionExpiry(e.target.value); setOptionStrike(''); }}
                  disabled={nfoInstruments.length === 0}
                  helperText={nfoLoading ? 'Loading expiries…' : (nfoInstruments.length === 0 ? 'Pick underlying first' : undefined)}
                >
                  {[...new Set(nfoInstruments.map((i: any) => i.expiry).filter(Boolean))].sort().map(exp => (
                    <MenuItem key={exp} value={exp}>{exp}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={[...new Set(
                    nfoInstruments
                      .filter((i: any) => i.expiry === optionExpiry && (i.instrument_type === 'CE' || i.instrument_type === 'PE'))
                      .map((i: any) => i.strike)
                  )].filter((s: any) => s > 0).sort((a: any, b: any) => a - b).map((s: any) => s.toString())}
                  value={optionStrike === '' ? '' : optionStrike.toString()}
                  onInputChange={(_, val) => setOptionStrike(val ? Number(val) : '')}
                  disabled={!optionExpiry}
                  renderInput={params => <TextField {...params} size="small" label="Strike *" helperText={!optionExpiry ? 'Pick expiry first' : undefined} />}
                />
              </Grid>
              <Grid item xs={4}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={optionType}
                  onChange={(_, v) => { if (v) setOptionType(v); }}
                  fullWidth
                  disabled={!optionStrike}
                >
                  <ToggleButton value="CE" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>CE (Call)</ToggleButton>
                  <ToggleButton value="PE" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>PE (Put)</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              {/* Resolved contract symbol display */}
              {optionExpiry && optionStrike && optionType && (
                <Grid item xs={12}>
                  <Box sx={{ p: 1, bgcolor: '#F5F3FF', borderRadius: 1, fontSize: '0.75rem' }}>
                    {(() => {
                      const match = nfoInstruments.find((i: any) =>
                        (i.name === optionUnderlying || i.tradingsymbol?.startsWith(optionUnderlying)) &&
                        i.expiry === optionExpiry &&
                        Number(i.strike) === Number(optionStrike) &&
                        i.instrument_type === optionType
                      );
                      return match ? (
                        <><strong>Contract:</strong> {match.tradingsymbol} · Lot size: {match.lot_size}</>
                      ) : (
                        <span style={{ color: '#DC2626' }}>No matching contract found for {optionUnderlying} {optionExpiry} {optionStrike} {optionType}</span>
                      );
                    })()}
                  </Box>
                </Grid>
              )}
            </>
          )}

          {/* Row 3: Qty + Lot Size (F&O) + Price */}
          <Grid item xs={isEquity ? 4 : 4}>
            <TextField fullWidth size="small" label={isEquity ? `${openLabel} Shares` : `${openLabel} Lots`} type="number" value={form.purchaseLots} onChange={e => updateField('purchaseLots', e.target.value ? Number(e.target.value) : '')} inputProps={{ min: 0 }} />
          </Grid>
          {isFnO && (
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Lot Size" type="number" value={form.lotSize} disabled InputProps={{ sx: { bgcolor: '#F5F3FF' } }} helperText={form.scrip && fnoScrips.find(s => s.symbol === form.scrip.toUpperCase()) ? 'From Kite API' : 'Select F&O scrip'} />
            </Grid>
          )}
          <Grid item xs={isEquity ? 4 : 4}>
            <TextField
              fullWidth
              size="small"
              label={`${openLabel} Price`}
              type="number"
              value={form.purchasePrice}
              onChange={e => {
                updateField('purchasePrice', e.target.value ? Number(e.target.value) : '');
                setPriceManuallyEdited(true);
              }}
              inputProps={{ min: 0, step: 0.01 }}
              helperText={priceManuallyEdited ? 'Manual price (will not be overridden)' : 'Auto-filled from live price'}
            />
          </Grid>
          {isEquity && (
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Stop Loss" type="number" value={form.stopLoss} onChange={e => updateField('stopLoss', e.target.value ? Number(e.target.value) : '')} inputProps={{ min: 0, step: 0.01 }} />
            </Grid>
          )}
          {isFnO && (
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Stop Loss" type="number" value={form.stopLoss} onChange={e => updateField('stopLoss', e.target.value ? Number(e.target.value) : '')} inputProps={{ min: 0, step: 0.01 }} />
            </Grid>
          )}

          {/* Close Trade toggle (new only) */}
          {(!isEdit || (isEdit && editTrade && editTrade.status === 'OPEN' && (editTrade.saleLots || 0) === 0)) && (
            <Grid item xs={isFnO ? 6 : 12}>
              <FormControlLabel
                control={<Switch checked={form.closeTrade} onChange={e => updateField('closeTrade', e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#0A0A0A' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#0A0A0A' } }} />}
                label={<Typography variant="body2" fontWeight={600}>Close Trade</Typography>}
              />
            </Grid>
          )}

          {/* Sale Section */}
          {showSaleSection && (
            <>
              <Grid item xs={12}>
                <Divider><Typography variant="caption" sx={{ fontWeight: 700, color: '#7C3AED', letterSpacing: 0.5 }}>{closeLabel.toUpperCase()} DETAILS</Typography></Divider>
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label={`${closeLabel} Date`} type="datetime-local" value={form.saleDate} onChange={e => updateField('saleDate', e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label={isEquity ? `${closeLabel} Shares` : `${closeLabel} Lots`} type="number" value={form.saleLots} onChange={e => updateField('saleLots', e.target.value ? Number(e.target.value) : '')} inputProps={{ min: 0 }} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label={`${closeLabel} Price`} type="number" value={form.salePrice} onChange={e => updateField('salePrice', e.target.value ? Number(e.target.value) : '')} inputProps={{ min: 0, step: 0.01 }} />
              </Grid>

              {/* Auto-split info */}
              {willAutoSplit && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ borderRadius: 2, fontSize: '0.8rem' }}>
                    This will create <strong>2 trades</strong>: {splitIntradayQty} shares as <strong>Intraday</strong> (sold same day) + {splitDeliveryQty} shares as <strong>Delivery</strong> (held)
                  </Alert>
                </Grid>
              )}

              {/* Charges FYI — Equity: primary (based on date) + alt scenario; F&O: single */}
              {chargesData && (
                <Grid item xs={12}>
                  {chargesData.isEquity && chargesData.primaryBuy ? (
                    <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#F5F3FF', borderRadius: 1.5, border: '1px solid #E9D5FF' }}>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {/* Primary: based on actual date */}
                        <Tooltip title={(() => {
                          const isIntra = chargesData.isIntraday;
                          const brkPct = (chargesData.primaryRate * 100).toFixed(7).replace(/\.?0+$/, '') + '%';
                          const sttLabel = isIntra
                            ? `STT (${(CHARGE_RATES.STT_EQUITY_INTRADAY_SELL * 100).toFixed(7).replace(/\.?0+$/, '')}% sell)`
                            : `STT (${(CHARGE_RATES.STT_EQUITY_DELIVERY_BUY * 100).toFixed(7).replace(/\.?0+$/, '')}% buy + ${(CHARGE_RATES.STT_EQUITY_DELIVERY_SELL * 100).toFixed(7).replace(/\.?0+$/, '')}% sell)`;
                          const exchRate = chargesData.exch === 'BSE' ? CHARGE_RATES.EXCHANGE_EQUITY_BSE : CHARGE_RATES.EXCHANGE_EQUITY_NSE;
                          const exchPct = (exchRate * 100).toFixed(5).replace(/\.?0+$/, '') + '%';
                          const sebiPct = (CHARGE_RATES.SEBI_RATE * 100).toFixed(4).replace(/\.?0+$/, '') + '%';
                          const stampRate = isIntra ? CHARGE_RATES.STAMP_EQUITY_INTRADAY : CHARGE_RATES.STAMP_EQUITY_DELIVERY;
                          const stampPct = (stampRate * 100).toFixed(4).replace(/\.?0+$/, '') + '%';
                          const gstPct = (CHARGE_RATES.GST_RATE * 100).toFixed(0) + '%';
                          return (
                          <Box sx={{ fontSize: '0.7rem', lineHeight: 1.6 }}>
                            <div style={{ fontWeight: 700, marginBottom: 2 }}>{chargesData.primaryLabel}</div>
                            <div><strong>Buy side:</strong></div>
                            <div>&nbsp; Brokerage ({brkPct}): ₹{chargesData.primaryBuy.brokerage.toFixed(7)}</div>
                            <div>&nbsp; {sttLabel}: ₹{chargesData.primaryBuy.stt.toFixed(7)}</div>
                            <div>&nbsp; Exchange ({exchPct}): ₹{chargesData.primaryBuy.exchangeCharges.toFixed(7)}</div>
                            <div>&nbsp; SEBI ({sebiPct}): ₹{chargesData.primaryBuy.sebiCharges.toFixed(7)}</div>
                            <div>&nbsp; Stamp ({stampPct}): ₹{chargesData.primaryBuy.stampDuty.toFixed(7)}</div>
                            <div>&nbsp; GST ({gstPct}): ₹{chargesData.primaryBuy.gst.toFixed(7)}</div>
                            <div>&nbsp; <strong>Buy Total: ₹{chargesData.primaryBuy.totalCharges.toFixed(7)}</strong></div>
                            {chargesData.primarySell && (<>
                              <div style={{ marginTop: 4 }}><strong>Sell side:</strong></div>
                              <div>&nbsp; Brokerage ({brkPct}): ₹{chargesData.primarySell.brokerage.toFixed(7)}</div>
                              <div>&nbsp; {sttLabel}: ₹{chargesData.primarySell.stt.toFixed(7)}</div>
                              <div>&nbsp; Exchange ({exchPct}): ₹{chargesData.primarySell.exchangeCharges.toFixed(7)}</div>
                              <div>&nbsp; SEBI ({sebiPct}): ₹{chargesData.primarySell.sebiCharges.toFixed(7)}</div>
                              <div>&nbsp; Stamp ({stampPct}): ₹{chargesData.primarySell.stampDuty.toFixed(7)}</div>
                              <div>&nbsp; GST ({gstPct}): ₹{chargesData.primarySell.gst.toFixed(7)}</div>
                              <div>&nbsp; <strong>Sell Total: ₹{chargesData.primarySell.totalCharges.toFixed(7)}</strong></div>
                              <div style={{ marginTop: 4, fontWeight: 700 }}>Total Charges: ₹{chargesData.primaryTotal!.totalCharges.toFixed(7)}</div>
                            </>)}
                          </Box>
                          );
                        })()} arrow placement="top">
                          <Typography variant="caption" sx={{ color: chargesData.primaryColor, fontWeight: 700, fontSize: '0.68rem', cursor: 'help' }}>
                            {chargesData.primaryLabel.split(' (')[0]}: ₹{chargesData.hasSale ? chargesData.primaryTotal!.totalCharges.toFixed(7) : chargesData.primaryBuy.totalCharges.toFixed(7)}
                            {!chargesData.hasSale && <Box component="span" sx={{ fontSize: '0.55rem', color: '#9CA3AF' }}> (buy)</Box>}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#9CA3AF', mt: 0.3, display: 'block' }}>
                        hover for buy/sell itemized breakdown
                      </Typography>
                    </Box>
                  ) : (
                    /* F&O: single charges */
                    <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#F5F3FF', borderRadius: 1.5, border: '1px solid #E9D5FF', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Tooltip title={(() => {
                        const cd = chargesData as any;
                        const isOpt = cd.isOption || cd.tradeType === TradeType.OPTION;
                        const isFut = cd.tradeType === TradeType.FUTURE;
                        const optPerLot = cd.optionPerLot || DEFAULT_OPTION_BROKERAGE_FALLBACK;
                        const lotsForBrk = cd.hasSale ? (cd.saleLots || 0) : (cd.purchaseLots || 0);

                        // Brokerage label
                        let brkLabel: string;
                        if (isOpt) {
                          brkLabel = cd.hasSale
                            ? `Brokerage (₹${optPerLot}/lot × ${lotsForBrk} buy + ${lotsForBrk} sell)`
                            : `Brokerage (₹${optPerLot} × ${cd.purchaseLots || 0} lots)`;
                        } else if (isFut) {
                          brkLabel = `Brokerage (${(CHARGE_RATES.DEFAULT_FUTURE_BROKERAGE * 100).toFixed(2).replace(/\.?0+$/, '')}%)`;
                        } else {
                          brkLabel = `Brokerage (${(cd.brokerageRate * 100).toFixed(7).replace(/\.?0+$/, '')}%)`;
                        }

                        // STT label
                        let sttLabel: string;
                        if (isFut) {
                          sttLabel = `STT (${(CHARGE_RATES.STT_FUTURE_SELL * 100).toFixed(7).replace(/\.?0+$/, '')}% sell)`;
                        } else if (isOpt) {
                          sttLabel = `STT (${(CHARGE_RATES.STT_OPTION_SELL * 100).toFixed(7).replace(/\.?0+$/, '')}% sell)`;
                        } else {
                          sttLabel = 'STT';
                        }

                        // Exchange label
                        const exchRate = isFut
                          ? (form.exchange === 'BSE' ? CHARGE_RATES.EXCHANGE_FUTURE_BSE : CHARGE_RATES.EXCHANGE_FUTURE_NSE)
                          : isOpt ? CHARGE_RATES.EXCHANGE_OPTION : 0;
                        const exchPct = (exchRate * 100).toFixed(5).replace(/\.?0+$/, '') + '%';

                        const sebiPct = (CHARGE_RATES.SEBI_RATE * 100).toFixed(4).replace(/\.?0+$/, '') + '%';
                        const stampRate = isFut ? CHARGE_RATES.STAMP_FUTURE : isOpt ? CHARGE_RATES.STAMP_OPTION : 0;
                        const stampPct = (stampRate * 100).toFixed(4).replace(/\.?0+$/, '') + '%';
                        const gstPct = (CHARGE_RATES.GST_RATE * 100).toFixed(0) + '%';

                        return (
                        <Box sx={{ fontSize: '0.7rem', lineHeight: 1.6 }}>
                          <div>{brkLabel}: ₹{cd.charges.brokerage.toFixed(7)}</div>
                          <div>{sttLabel}: ₹{cd.charges.stt.toFixed(7)}</div>
                          <div>Exchange ({exchPct}): ₹{cd.charges.exchangeCharges.toFixed(7)}</div>
                          <div>SEBI ({sebiPct}): ₹{cd.charges.sebiCharges.toFixed(7)}</div>
                          <div>Stamp ({stampPct}): ₹{cd.charges.stampDuty.toFixed(7)}</div>
                          <div>GST ({gstPct}): ₹{cd.charges.gst.toFixed(7)}</div>
                        </Box>
                        );
                      })()} arrow placement="top">
                        <Typography variant="caption" sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '0.7rem', cursor: 'help', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          {(chargesData as any).isOption ? 'Options' : 'Futures'} {(chargesData as any).purchaseLots} lots{(chargesData as any).lotSize > 1 ? ` × ${(chargesData as any).lotSize} = ${(chargesData as any).purchaseLots * (chargesData as any).lotSize} shares` : ''}
                          {' | '}Est. Charges{showSaleSection ? '' : ' (buy)'}: ₹{(chargesData as any).charges.totalCharges.toFixed(7)}
                          <Box component="span" sx={{ fontSize: '0.6rem', color: '#9CA3AF', ml: 0.3 }}>(hover)</Box>
                        </Typography>
                      </Tooltip>
                    </Box>
                  )}
                </Grid>
              )}
            </>
          )}
        </Grid>

        {/* Breakeven info — shown when editing or when breakeven prop is passed */}
        {breakeven && (breakeven.intradayBE || breakeven.deliveryBE || breakeven.fullClearBE || breakeven.carryForwardBE || breakeven.futureBE || breakeven.optionBE) && (
          <Box sx={{ mt: 2, px: 1.5, py: 1, bgcolor: '#F0FDF4', borderRadius: 1.5, border: '1px solid #BBF7D0' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#059669', letterSpacing: 0.3, fontSize: '0.6rem', textTransform: 'uppercase' }}>Breakeven Rates</Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
              {breakeven.intradayBE && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.55rem' }}>Intra</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#059669' }}>₹{breakeven.intradayBE.toFixed(2)}</Typography>
                </Box>
              )}
              {breakeven.deliveryBE && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.55rem' }}>Delivery</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#2563EB' }}>₹{breakeven.deliveryBE.toFixed(2)}</Typography>
                </Box>
              )}
              {breakeven.fullClearBE && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.55rem' }}>Full Clear</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#DC2626' }}>₹{breakeven.fullClearBE.toFixed(2)}</Typography>
                </Box>
              )}
              {breakeven.carryForwardBE && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.55rem' }}>Carry Fwd</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#D97706' }}>₹{breakeven.carryForwardBE.toFixed(2)}</Typography>
                </Box>
              )}
              {breakeven.futureBE && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.55rem' }}>Future BE</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>₹{breakeven.futureBE.toFixed(2)}</Typography>
                </Box>
              )}
              {breakeven.optionBE && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.55rem' }}>Option BE</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>₹{breakeven.optionBE.toFixed(2)}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none', color: '#6B7280' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ textTransform: 'none', bgcolor: '#0A0A0A', boxShadow: 'none', borderRadius: 2, px: 3, '&:hover': { bgcolor: '#262626', boxShadow: 'none' } }}>
          {loading ? 'Saving...' : isEdit ? 'Update Trade' : 'Add Trade'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TradeDialog;
